import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.')
}

// Validate URL format
try {
  new URL(supabaseUrl)
} catch (error) {
  throw new Error(`Invalid Supabase URL format: ${supabaseUrl}. Please check your VITE_SUPABASE_URL in .env file.`)
}

// Check for placeholder values
if (supabaseUrl.includes('your-project-id') || supabaseAnonKey.includes('your_supabase_anon_key')) {
  console.warn('⚠️  Using placeholder Supabase credentials. Please update your .env file with actual values from your Supabase project.')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          username: string | null
          full_name: string | null
          bio: string | null
          avatar_url: string | null
          location: string | null
          is_expert: boolean
          health_goals: string[]
          interests: string[]
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          username?: string | null
          full_name?: string | null
          bio?: string | null
          avatar_url?: string | null
          location?: string | null
          is_expert?: boolean
          health_goals?: string[]
          interests?: string[]
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          username?: string | null
          full_name?: string | null
          bio?: string | null
          avatar_url?: string | null
          location?: string | null
          is_expert?: boolean
          health_goals?: string[]
          interests?: string[]
          updated_at?: string
        }
      }
      posts: {
        Row: {
          id: string
          author_id: string
          content: string
          category: string
          tags: string[]
          is_anonymous: boolean
          images: string[]
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          author_id: string
          content: string
          category?: string
          tags?: string[]
          is_anonymous?: boolean
          images?: string[]
          created_at?: string
          updated_at?: string
        }
        Update: {
          content?: string
          category?: string
          tags?: string[]
          is_anonymous?: boolean
          images?: string[]
          updated_at?: string
        }
      }
      comments: {
        Row: {
          id: string
          post_id: string
          author_id: string
          content: string
          is_expert_answer: boolean
          created_at: string
        }
        Insert: {
          id?: string
          post_id: string
          author_id: string
          content: string
          is_expert_answer?: boolean
          created_at?: string
        }
        Update: {
          content?: string
          is_expert_answer?: boolean
        }
      }
      reactions: {
        Row: {
          id: string
          user_id: string
          post_id: string | null
          comment_id: string | null
          type: string
          created_at: string
        }
        Insert: {
          id?: string
          user_id: string
          post_id?: string | null
          comment_id?: string | null
          type: string
          created_at?: string
        }
        Update: {
          type?: string
        }
      }
      follows: {
        Row: {
          id: string
          follower_id: string
          following_id: string
          created_at: string
        }
        Insert: {
          id?: string
          follower_id: string
          following_id: string
          created_at?: string
        }
        Update: never
      }
      articles: {
        Row: {
          id: string
          title: string
          excerpt: string | null
          content: string
          author_id: string
          category: string
          tags: string[]
          read_time: number
          featured: boolean
          views: number
          likes: number
          cover_image: string | null
          published_at: string
          created_at: string
        }
        Insert: {
          id?: string
          title: string
          excerpt?: string | null
          content: string
          author_id: string
          category?: string
          tags?: string[]
          read_time?: number
          featured?: boolean
          views?: number
          likes?: number
          cover_image?: string | null
          published_at?: string
          created_at?: string
        }
        Update: {
          title?: string
          excerpt?: string | null
          content?: string
          category?: string
          tags?: string[]
          read_time?: number
          featured?: boolean
          views?: number
          likes?: number
          cover_image?: string | null
          published_at?: string
        }
      }
      products: {
        Row: {
          id: string
          name: string
          category: string
          description: string | null
          price: number
          images: string[]
          in_stock: boolean
          stock_count: number
          prescription_required: boolean
          manufacturer: string | null
          rating: number
          reviews_count: number
          tags: string[]
          created_at: string
        }
        Insert: {
          id?: string
          name: string
          category: string
          description?: string | null
          price: number
          images?: string[]
          in_stock?: boolean
          stock_count?: number
          prescription_required?: boolean
          manufacturer?: string | null
          rating?: number
          reviews_count?: number
          tags?: string[]
          created_at?: string
        }
        Update: {
          name?: string
          category?: string
          description?: string | null
          price?: number
          images?: string[]
          in_stock?: boolean
          stock_count?: number
          prescription_required?: boolean
          manufacturer?: string | null
          rating?: number
          reviews_count?: number
          tags?: string[]
        }
      }
    }
  }
}